import type { ComponentChildren } from "preact";
import { ConvexProvider } from "convex/react";
import { createContext } from "preact";
import { useContext, useEffect, useMemo } from "preact/hooks";

import type { Edition, WidgetConfig } from "../types";
import { getConvexClient } from "../lib/convex";
import { injectTheme } from "../lib/theme";
import { useEditionData } from "./useWidgetQueries";

type EditionContextValue = {
  edition: Edition | null;
  editionId: string;
  loading: boolean;
  basePath: string;
  detailsBasePath: string;
  mode: "artists" | "schedules" | "events" | "places";
  defaultView: "by-place" | "by-time";
  timeFormat: "24h" | "12h";
  locale: "en" | "fr" | "es";
  routingMode: "path" | "hash";
  /** Category ids the block/shortcode is locked to (OR filter). Empty = no lock. */
  lockedCategoryIds: string[];
  categoryName?: string;
  /** Place type ids the block/shortcode is locked to (OR filter). Empty = no lock. */
  lockedPlaceTypeIds: string[];
  placeTypeName?: string;
};

export const EditionContext = createContext<EditionContextValue | null>(null);

type EditionProviderProps = {
  config: WidgetConfig;
  containerRef: preact.RefObject<HTMLElement>;
  children: ComponentChildren;
};

export function EditionProvider({
  config,
  containerRef,
  children,
}: EditionProviderProps) {
  const client = getConvexClient();

  const inner = (
    <EditionProviderInner config={config} containerRef={containerRef}>
      {children}
    </EditionProviderInner>
  );

  // ConvexProvider from convex/react uses React types but works with preact
  return (
    <ConvexProvider client={client}>{inner as React.ReactNode}</ConvexProvider>
  );
}

function EditionProviderInner({
  config,
  containerRef,
  children,
}: EditionProviderProps) {
  const { data: edition, loading } = useEditionData(config.editionId);

  // Normalize the locked category selection to an array. Prefer the new
  // multi-category `categoryIds`; fall back to the legacy single `categoryId`.
  const lockedCategoryIds = useMemo(
    () =>
      config.categoryIds && config.categoryIds.length > 0
        ? config.categoryIds
        : config.categoryId
          ? [config.categoryId]
          : [],
    [config.categoryIds, config.categoryId],
  );

  // Same normalization for place types: prefer the multi `placeTypeIds`,
  // fall back to the legacy single `placeTypeId`.
  const lockedPlaceTypeIds = useMemo(
    () =>
      config.placeTypeIds && config.placeTypeIds.length > 0
        ? config.placeTypeIds
        : config.placeTypeId
          ? [config.placeTypeId]
          : [],
    [config.placeTypeIds, config.placeTypeId],
  );

  useEffect(() => {
    // Design colors may be in config.design.colors or config.colors
    const colors = edition?.design?.colors;
    if (containerRef.current && colors) {
      injectTheme(colors, containerRef.current);
    }
  }, [edition, containerRef]);

  const value: EditionContextValue = {
    edition: edition as Edition | null,
    editionId: config.editionId,
    loading,
    basePath: config.basePath,
    detailsBasePath: config.detailsBasePath,
    mode: config.mode,
    defaultView: config.defaultView ?? "by-place",
    timeFormat: config.timeFormat ?? "24h",
    locale: config.locale,
    routingMode: config.routingMode ?? "path",
    lockedCategoryIds,
    categoryName: config.categoryName,
    lockedPlaceTypeIds,
    placeTypeName: config.placeTypeName,
  };

  return (
    <EditionContext.Provider value={value}>{children}</EditionContext.Provider>
  );
}

export function useEdition() {
  const context = useContext(EditionContext);
  if (!context) {
    throw new Error("useEdition must be used within EditionProvider");
  }
  return context;
}
